import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Loader2, Lock, TrendingUp } from "lucide-react";
import {
  applyShareMeta,
  fetchSharedBlueprint,
  recordShareEvent,
  requestSharePdf,
  shareCardImageUrl,
  storeShareAttribution,
  shareUrl,
  type SharedBlueprint,
} from "@/lib/share";
import { readCampaignFromUrl } from "@/lib/shareCampaigns";

export default function SharedBlueprint() {
  const { slug = "" } = useParams();
  const location = useLocation();
  const [data, setData] = useState<SharedBlueprint | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    const campaign = readCampaignFromUrl(location.search);
    fetchSharedBlueprint(slug, true, campaign)
      .then((d) => {
        if (!active) return;
        setData(d);
        applyShareMeta({
          title: d.headline || d.preview.title,
          description: d.description,
          image: d.og_image_url || shareCardImageUrl(d.slug),
          url: shareUrl(d.slug),
        });
        // Credit this link if the visitor goes on to sign up and buy.
        storeShareAttribution(d.slug, {
          ...campaign,
          variant_key: d.variant_key ?? campaign.variant_key ?? null,
        });
        // The blueprint-share endpoint already records the campaign-tagged view.
      })
      .catch((e) => active && setError((e as Error).message));
    return () => {
      active = false;
    };
  }, [slug, location.search]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const result = await requestSharePdf(slug);
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      const err = e as Error & { code?: string };
      toast.error(
        err.code === "auth_required"
          ? "Sign in to generate this blueprint PDF"
          : err.code === "no_credits"
            ? "No blueprint download credits left"
            : err.message,
      );
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="font-display text-2xl font-bold">This blueprint isn't available</h1>
        <p className="text-muted-foreground">{error}</p>
        <Button asChild>
          <Link to="/">Explore TrendsDiscovery</Link>
        </Button>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  const p = data.preview;

  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-5 py-10 sm:py-16">
        <Link to="/" className="font-mono text-[11px] uppercase tracking-widest text-primary">
          TrendsDiscovery
        </Link>

        <header className="mt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-mono text-[11px] uppercase text-muted-foreground">
                shared blueprint {p.niche ? `· ${p.niche}` : ""} · {data.view_count} views
              </p>
              <h1 className="font-display text-3xl font-bold mt-1">{p.title}</h1>
              <p className="text-muted-foreground mt-2">{p.summary}</p>
            </div>
            <div className="text-right shrink-0">
              <div className="font-display text-4xl font-bold text-primary flex items-center gap-1">
                <TrendingUp className="size-5" />
                {p.scores.total}
              </div>
              <div className="font-mono text-[10px] text-muted-foreground">total score</div>
            </div>
          </div>
        </header>

        <section className="grid gap-3 sm:grid-cols-3 mt-6">
          {[
            { label: "Velocity", value: p.scores.velocity },
            { label: "Volume", value: p.scores.volume },
            { label: "Relevance", value: p.scores.relevance },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border border-border bg-card p-3">
              <div className="flex justify-between font-mono text-[10px] uppercase text-muted-foreground">
                <span>{s.label}</span>
                <span>{s.value}</span>
              </div>
              <div className="h-1.5 bg-secondary rounded-full overflow-hidden mt-2">
                <div className="h-full bg-primary" style={{ width: `${s.value}%` }} />
              </div>
            </div>
          ))}
        </section>

        <p className="font-mono text-[11px] text-muted-foreground mt-4">
          {p.signal_count} signals · {p.sources.length} sources
          {p.sources.length ? `: ${p.sources.join(", ")}` : ""}
        </p>

        {p.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {p.tags.map((t) => (
              <span
                key={t}
                className="font-mono text-[10px] rounded-full border border-border px-2 py-0.5 text-muted-foreground"
              >
                #{t}
              </span>
            ))}
          </div>
        )}

        {(p.target_buyer || p.target_audience || p.product_concept) && (
          <section className="mt-8 rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-bold">Who this is for</h2>
            <dl className="mt-3 space-y-2 text-sm">
              {p.target_buyer && (
                <div>
                  <dt className="inline font-semibold">Buyer: </dt>
                  <dd className="inline text-muted-foreground">{p.target_buyer}</dd>
                </div>
              )}
              {p.target_audience && (
                <div>
                  <dt className="inline font-semibold">Audience: </dt>
                  <dd className="inline text-muted-foreground">{p.target_audience}</dd>
                </div>
              )}
              {p.product_concept && (
                <div>
                  <dt className="inline font-semibold">Concept: </dt>
                  <dd className="inline text-muted-foreground">{p.product_concept}</dd>
                </div>
              )}
              {(p.format || p.price_range) && (
                <div>
                  <dt className="inline font-semibold">Shape: </dt>
                  <dd className="inline text-muted-foreground">
                    {p.format ?? "n/a"} · {p.price_range ?? "n/a"}
                  </dd>
                </div>
              )}
            </dl>
          </section>
        )}

        {p.keyword_ideas.length > 0 && (
          <section className="mt-4 rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-bold">Keyword ideas</h2>
            <div className="flex flex-wrap gap-1 mt-3">
              {p.keyword_ideas.map((k) => (
                <span
                  key={k}
                  className="font-mono text-[10px] rounded-full border border-border px-2 py-0.5 text-muted-foreground"
                >
                  {k}
                </span>
              ))}
            </div>
          </section>
        )}

        {p.first_steps.length > 0 && (
          <section className="mt-4 rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-bold">First launch steps</h2>
            <ol className="list-decimal list-inside mt-3 space-y-1 text-sm text-muted-foreground">
              {p.first_steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          </section>
        )}

        <section className="mt-4 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-5">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <Lock className="size-4 text-primary" />
            Included with a license
          </h2>
          <ul className="mt-3 space-y-1 text-sm text-muted-foreground list-disc list-inside">
            {p.locked.remaining_steps > 0 && <li>{p.locked.remaining_steps} further launch steps</li>}
            {p.locked.detailed_blueprint && (
              <li>Detailed blueprint: audience segments, content plan, pricing tiers, checklist</li>
            )}
            {p.locked.competitor_overlay && <li>Competitor overlay and gap analysis</li>}
            <li>Live alerts when new trends match this niche</li>
          </ul>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button asChild>
              <Link to="/#pricing">Get lifetime access</Link>
            </Button>
            <Button variant="outline" onClick={downloadPdf} disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              Download PDF
            </Button>
          </div>
        </section>

        <p className="font-mono text-[10px] text-muted-foreground mt-8">
          Shared {new Date(data.created_at).toLocaleDateString()} · trendsdiscovery.com
        </p>
      </div>
    </main>
  );
}
